import { useCallback, useEffect, useState } from 'react';

import api from './api';

/**
 * Catalogo de equipamentos de aplicacao da conta.
 *
 * A lista e mantida num cache de modulo porque o campo vive dentro de
 * `relation.js`, que e renderizado uma vez por relacao (produto+praga+cultura)
 * da receita - e em emissao em lote isso pode ser dezenas de instancias. Sem o
 * cache, cada uma dispararia seu proprio GET, e criar um equipamento numa
 * relacao nao apareceria nas outras.
 *
 * Todas as instancias assinam o mesmo estado, então criar/renomear/remover em
 * qualquer uma reflete em todas na hora.
 */

const ROTA = '/equipamentos-aplicacao';

let cache = {
  equipamentos: [],
  carregado: false,
  carregando: false,
  erro: false,
};

const inscritos = new Set();

const notificar = () => {
  inscritos.forEach((fn) => fn(cache));
};

const setCache = (parcial) => {
  cache = { ...cache, ...parcial };
  notificar();
};

const ordenar = (lista) =>
  [...lista].sort((a, b) =>
    String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'),
  );

export const buscarEquipamentos = async ({ force = false } = {}) => {
  if (cache.carregando) return cache.equipamentos;
  if (cache.carregado && !force) return cache.equipamentos;

  setCache({ carregando: true, erro: false });
  try {
    const response = await api.get(ROTA);
    const lista = response?.data?.data;
    setCache({
      equipamentos: ordenar(Array.isArray(lista) ? lista : []),
      carregado: true,
      carregando: false,
      erro: false,
    });
  } catch (error) {
    // Nao bloqueia a emissao: o campo continua utilizavel, so sem sugestoes.
    setCache({ carregando: false, carregado: false, erro: true });
  }

  return cache.equipamentos;
};

export const criarEquipamento = async (nome) => {
  const response = await api.post(ROTA, { nome });
  const criado = response?.data?.data;
  if (!criado) return null;

  // A API reaproveita registro existente (inclusive reativando inativo), entao
  // aqui pode vir um id que ja esta na lista - substitui em vez de duplicar.
  const semDuplicata = cache.equipamentos.filter((e) => e.id !== criado.id);
  setCache({ equipamentos: ordenar([...semDuplicata, criado]) });

  return criado;
};

export const editarEquipamento = async (id, nome) => {
  const response = await api.put(`${ROTA}/${id}`, { nome });
  const atualizado = response?.data?.data;
  if (!atualizado) return null;

  setCache({
    equipamentos: ordenar(
      cache.equipamentos.map((e) => (e.id === id ? { ...e, ...atualizado } : e)),
    ),
  });

  return atualizado;
};

export const removerEquipamento = async (id) => {
  await api.delete(`${ROTA}/${id}`);
  // A API inativa (ativo = 0); a lista só mostra ativos, então sai daqui.
  setCache({ equipamentos: cache.equipamentos.filter((e) => e.id !== id) });
};

/** Usado ao trocar de conta/usuario, para nao vazar lista entre sessoes. */
export const limparCacheEquipamentos = () => {
  setCache({ equipamentos: [], carregado: false, carregando: false, erro: false });
};

export const useEquipamentosAplicacao = () => {
  const [estado, setEstado] = useState(cache);

  useEffect(() => {
    inscritos.add(setEstado);
    setEstado(cache);
    buscarEquipamentos();

    return () => {
      inscritos.delete(setEstado);
    };
  }, []);

  const recarregar = useCallback(() => buscarEquipamentos({ force: true }), []);

  return {
    equipamentos: estado.equipamentos,
    loading: estado.carregando,
    erro: estado.erro,
    criar: criarEquipamento,
    editar: editarEquipamento,
    remover: removerEquipamento,
    recarregar,
  };
};
